import {
	getConfig,
	isPreviewEnvironment,
	isProductionEnvironment,
} from "merchant-portal-frontend/src/config";
import { makeBreadApiClient } from "merchant-portal-frontend/src/model/client/http";

import { HTTPClient } from "./api/http";

enum LogLevel {
	Error = "error",
	Warn = "warn",
	Info = "info",
}

interface LogEvent {
	level: LogLevel;
	message: string;
	context?: object;
}

type LogRequest = Omit<LogEvent, "level">;

interface Logger {
	error: (event: LogRequest) => Promise<void>;
	warn: (event: LogRequest) => Promise<void>;
	info: (event: LogRequest) => Promise<void>;
}

export class BrowserLogger implements Logger {
	private client: HTTPClient;

	private endpoint: string;

	private shouldConsoleLog: boolean;

	constructor() {
		this.client = makeBreadApiClient();
		this.endpoint = `${getConfig("apiurl")}/api/merchant-portal-backend/log`;
		this.shouldConsoleLog = !isPreviewEnvironment() && !isProductionEnvironment();
	}

	async error(event: LogRequest) {
		return this.postLog({
			level: LogLevel.Error,
			...event,
		});
	}

	async warn(event: LogRequest) {
		return this.postLog({
			level: LogLevel.Warn,
			...event,
		});
	}

	async info(event: LogRequest) {
		return this.postLog({
			level: LogLevel.Info,
			...event,
		});
	}

	private async postLog(event: LogEvent) {
		this.logToConsole(event);
		const { status } = await this.client.post<void, LogEvent>({
			url: this.endpoint,
			isAuthenticated: false,
			payload: event,
		});

		if (status !== 200) {
			throw new Error(`failed to post error log with status ${status}`);
		}
	}

	private logToConsole({ level, message, context }: LogEvent) {
		if (!this.shouldConsoleLog) {
			return;
		}

		// eslint-disable-next-line no-console
		console[level](message, context);
	}
}

export function makeBrowserLogs() {
	return {
		logger: new BrowserLogger(),
	};
}
